/**
 * Telegram: the Profile linking block (mockup `docs/mockups/phase-2/profile.html`) and every line
 * the office bot writes into a chat. Both live here because both are user-facing Ukrainian text and
 * neither belongs in code — the bot renders through the same dictionary as the panel
 * (`apps/worker/src/telegram/i18n.ts`).
 *
 * `bot.*` strings are plain text: the bot sends messages without `parse_mode`, so nothing here is
 * escaped or interpreted as markup, and a draft that contains `*` or `_` arrives as written.
 * Placeholders are `{name}` and are replaced by the caller.
 */
export const telegram = {
  // ---- Profile page block ----
  'profile.telegram': 'Telegram',
  'profile.telegram.intro': 'Прив’яжи Telegram, щоб отримувати запити на затвердження та денний підсумок прямо в чат.',
  'profile.telegram.getCode': 'Отримати код',
  'profile.telegram.newCode': 'Новий код',
  'profile.telegram.expires': 'діє 10 хвилин',
  'profile.telegram.expiresAt': 'діє до {time}',
  'profile.telegram.howto': 'Надішли боту команду',
  'profile.telegram.linked': 'Прив’язано: так, чат …{suffix}',
  'profile.telegram.notLinked': 'Не прив’язано',
  'profile.telegram.unlink': 'Відв’язати',
  'profile.telegram.error': 'Не вдалося. Спробуй ще раз.',

  // ---- bot: linking ----
  'bot.start': 'Це офісний бот brao. Він надсилає запити на затвердження і денний підсумок.\n\nЩоб прив’язати чат, відкрий Профіль у панелі, натисни «Отримати код» і надішли сюди: /link КОД',
  'bot.link.usage': 'Надішли код одним повідомленням: /link A7K2M9QX',
  'bot.link.invalid': 'Код не підійшов: він неправильний або вже недійсний. Візьми новий у профілі.',
  'bot.link.taken': 'Цей чат уже прив’язано до іншого облікового запису. Спершу відв’яжи його в профілі.',
  'bot.link.ok': 'Готово. Чат прив’язано до {name}. Тепер запити на затвердження приходитимуть сюди.',
  'bot.unknownChat': 'Цей чат не прив’язано. Відкрий Профіль у панелі, натисни «Отримати код» і надішли сюди: /link КОД',
  'bot.unknownCommand': 'Не зрозумів. Доступні команди: /start, /link КОД.',

  // ---- bot: approval request ----
  'bot.approval.step': 'Результат прогону чекає на рішення',
  'bot.approval.tool': 'Агент просить дозволу на дію',
  'bot.approval.cost': 'Прогін перевищив поріг вартості',
  'bot.approval.other': 'Запит на затвердження',
  'bot.approval.task': 'Задача: {taskKey}',
  'bot.approval.agent': 'Агент: {agent}',
  'bot.approval.tool.name': 'Інструмент: {tool}',
  'bot.approval.tool.args': 'Аргументи: {args}',
  'bot.approval.reason': 'Причина: {reason}',
  'bot.approval.cost.line': 'Оцінка: {cost} {currency}, поріг: {threshold} {currency}',
  'bot.approval.cost.spent': 'Уже витрачено: {cost} {currency}',
  'bot.approval.link': 'У панелі: {url}',
  'bot.approval.truncated': '… (скорочено)',

  // ---- bot: reasons the engine gives ----
  'bot.reason.irreversible': 'незворотна дія',
  'bot.reason.retryInFlight': 'дія обірвалася на середині, повтор потребує рішення',
  'bot.reason.costThreshold': 'вартість прогону перевищила поріг',

  // ---- bot: buttons and decisions ----
  'bot.button.approve': 'Погодити',
  'bot.button.rework': 'На доопрацювання',
  'bot.button.reject': 'Відхилити',
  'bot.decision.approved': 'Рішення: погоджено ({who})',
  'bot.decision.rework': 'Рішення: на доопрацювання ({who}) — {reason}',
  'bot.decision.rejected': 'Рішення: відхилено ({who})',
  'bot.decision.done': 'Записано.',
  'bot.decision.already': 'Вже вирішено',
  'bot.decision.expired': 'Вже вирішено: запит протерміновано',
  'bot.decision.notAllowed': 'Немає права вирішувати',
  'bot.decision.failed': 'Не вдалося записати рішення',
  'bot.rework.ask': 'Напиши коментар одним повідомленням',
  'bot.rework.expired': 'Час на коментар вийшов. Натисни кнопку ще раз.',
  'bot.rework.started': 'Новий прогін запущено.',

  // ---- bot: run summaries ----
  'bot.run.line': '{taskKey} · {agent} · {status} · {cost} {currency}',
  'bot.run.status.completed': 'завершено',
  'bot.run.status.failed': 'помилка',
  'bot.run.status.expired': 'протерміновано',
  'bot.run.status.cancelled': 'скасовано',

  // ---- bot: daily summary ----
  'bot.daily.title': 'Підсумок за {date}',
  'bot.daily.runs': 'Прогонів: {count} (завершено {done}, з помилкою {failed})',
  'bot.daily.cost': 'Вартість: {cost} {currency}',
  'bot.daily.pending': 'Чекають рішення: {count}, найстаріший {age}',
  'bot.daily.pendingNone': 'Запитів на затвердження немає.',
  'bot.age.hours': '{h} год',
  'bot.age.days': '{d} дн',
  'bot.age.minutes': '{m} хв',
  'bot.weekly.title': 'Тижневий перегляд',
  'bot.weekly.uncertain': 'Непевне у Вхідній ({count}):',
  'bot.weekly.uncertainNone': 'Непевного у Вхідній немає.',
  'bot.weekly.more': '…і ще {count}',
  'bot.weekly.counts': 'Нерозібраного: {unsorted} · Задач чекають вас: {waiting}',
  'bot.weekly.link': 'Усе на Огляді: {url}',
} as const;
